<script setup>
  import { ref, computed, onMounted, watchEffect } from 'vue';
  import { useProForecastStore } from '@/stores/ProForecastStore';
  import { useUserStore } from "@/stores/UserStore";
  import { useDateHelper } from "@/composables/useDateHelper";
  import DataContainer from '@/components/DataContainer.vue';
  import DataContainerSection from '@/components/DataContainerSection.vue';  
  import ProForecastListItem from '@/components/ProForecastListItem.vue';
  import ProForecastListForecasterPhoto from '@/components/ProForecastListForecasterPhoto.vue';
  import ModalSiteSettings from '@/components/ModalSiteSettings.vue';
  import ModalBrandPromo from '@/components/ModalBrandPromo.vue';
  import useSettingsGlobals from '@/composables/useSettingsGlobals';
  import Icon from '@/components/icons/Icon.vue';
  import { format } from 'date-fns';

  const { $PROMO_MODAL_ALLOWED } = useSettingsGlobals();   

  const PATHNAME = window.location.pathname;
  const REGION_ORDER = ['northeast', 'southeast', 'florida and gulf coast', 'great lakes', 'northwest', 'california'];

  const proForecastStore = useProForecastStore();

  const dateHelper = useDateHelper();

  const userStore = useUserStore();

  await proForecastStore.getAvailableProForecasts();
  await proForecastStore.getForecasters();

  const isExtended = ref(false);

  const showPromoModal = computed(() => userStore.messages && userStore.messages.length > 0 && parseInt(userStore.nonTrialMemberLevel) < 49 && $PROMO_MODAL_ALLOWED);
  
  const forecastsByRegion = computed(() => {
    const forecasts = [];
    const unorderedRegionForecasts = [];
    const sortedForecasts = Object.entries(proForecastStore.availableProForecastsByRegion);

    for (let forecast of sortedForecasts) {
      const forecastRegion = forecast[0]
      if (!isExtended.value && forecastRegion.includes('Extended')) continue;
      if (isExtended.value && !forecastRegion.includes('Extended')) continue;

      const forecastRegionBase = forecastRegion.includes('Extended') ? forecastRegion.replace("Extended ", "") : forecastRegion;
      const forecastIndex = REGION_ORDER.indexOf(forecastRegionBase.toLowerCase());

      if (forecastIndex === -1) {
        unorderedRegionForecasts.push({ forecasts: [...forecast[1]], region: forecastRegion })
        continue;
      }

      forecasts[forecastIndex] = { 
        forecasts: [...forecast[1].forecasts], 
        forecasters:  [...forecast[1].forecasters],
        region: forecastRegion }
    }
    
    return [...forecasts, ...unorderedRegionForecasts];
  })

  const forecastsByRegionActiveFirst = computed(() => {
    return forecastsByRegion.value.map(region => {
      const activeForecasts = region.forecasts.filter(forecast => forecast.season_status === 'active')
      const inactiveForecasts = region.forecasts.filter(forecast => forecast.season_status !== 'active')

      return {
        ...region,
        forecasts: [...activeForecasts, ...inactiveForecasts]
      }
    })
  }) 

  function getForecasterImageFile(forecasterId) {
    return proForecastStore.forecasterData.find(forecaster => forecaster.id === forecasterId)?.photo_filename ?? null
  }

  function getForecasterInitals(forecasterId) {
    return proForecastStore.getForecaster(forecasterId)?.name.split(" ").map(namePart => namePart[0]).join("")
  } 

  function getRegionName(regionString) {
    if (regionString.includes('Extended')) return regionString.split('Extended')[1].trim()
    return regionString;
  }

  function formatUpatedTime(timestamp) {
    const localTimestampISOFormatted = dateHelper.datetimeToISOFormat(timestamp);
    const date = new Date(localTimestampISOFormatted);

    return `${format(date, "eee, LLL d")} at ${format(date, "p")}`;
  }

  onMounted(() => {
    isExtended.value = PATHNAME.includes('extended') || new URLSearchParams(window.location.search).get('extended')

    window.addEventListener('popstate', () => {
      isExtended.value = PATHNAME.includes('extended') || new URLSearchParams(window.location.search).get('extended') === 'true'
    })
  })

  watchEffect(() => {
    if (isExtended.value) {
      document.title = 'Extended Pro Forecasts'
      if (import.meta.env.DEV) {
        history.pushState({}, '', `${PATHNAME}?extended=true`)
        return;
      }
      history.pushState({}, '', `${PATHNAME}/extended`)
    } else {
      document.title = 'Pro Forecasts'
      history.pushState({}, '', PATHNAME)
    }
  })
</script>


<template>
  <div class="jwx-page" data-container-gap="true">
    <div class="jwx-page__container" data-section-gap="true" data-page-width="md">
      <header class="jwx-toolbar-header">
        <div class="jwx-toolbar-header__main-content">
          <div class="jwx-toolbar-header__title">
            <h2 class="jwx-toolbar-header__title-text">Pro{{ isExtended ? ' Extended ' : '' }} Forecasts</h2>
          </div>
        </div>
      </header> 
    </div>

    <div v-if="proForecastStore.recentlyViewedProForecasts.length > 0 && !isExtended" class="jwx-page__container" data-section-gap="true" data-page-width="md">
      <DataContainerSection
        :sectionOverflow="false"
      >
        <template #title>Recently Viewed Pro Forecasts</template>
        <template #content>
          <DataContainer>
            <div class="jwx-data-container__content">
              <div class="jwx-spot-list-container">

                <ul class="jwx-pro-forecast-list">
                  <ProForecastListItem
                    v-for="forecast in proForecastStore.recentlyViewedProForecasts"
                    :key="forecast.forecast_id"
                    :forecast="forecast"
                    :showForecasterPhoto="true"
                    :forecasterImageFile="getForecasterImageFile(forecast.forecaster_member_id)"
                  >
                    <template #subtitle>
                      <p v-if="forecast.season_status === 'active'" class="jwx-pro-forecast-list__forecast-headline">{{ forecast.headline }}</p>
                    </template>
                  </ProForecastListItem>
                </ul>
              </div>           
            </div>
          </DataContainer>
        </template>
      </DataContainerSection>
    </div>
    
    <div class="jwx-page__container" data-section-margin="true" data-page-width="md">
      <h2
        class="jwx-page-block-title"
        :class="{ 'jwx-m-top-jumbo': proForecastStore.recentlyViewedProForecasts.length > 0 }">
          Professional Meterologist{{ isExtended ? ' Extended ' : '' }} Forecasts
        </h2>
      <DataContainerSection
        v-for="{ forecasts, forecasters, region } in forecastsByRegionActiveFirst" 
        :key="region"
        :sectionOverflow="false"
      >
        <template #title>
          <span class="jwx-font-weight-400">{{ getRegionName(region) }}</span>
        </template>
        <template #metaActions>
          <div 
          class="jwx-pro-forecast-list__forecaster-avatar-group"
          >
          <ProForecastListForecasterPhoto
            v-for="forecasterId in forecasters" 
            :forecaster-image-file="getForecasterImageFile(forecasterId)" 
            :key="forecasterId"
            size="lg"
            />
          </div>
        </template>
        <template #content>
          <DataContainer>
            <div class="jwx-data-container__content">
              <div class="jwx-spot-list-container">

                <ul class="jwx-pro-forecast-list">
                  <ProForecastListItem
                    v-for="forecast in forecasts"
                    :key="forecast.forecast_id"
                    :forecast="forecast"
                  >
                    <template #subtitle>
                      <p v-if="forecast.season_status === 'active'" class="jwx-pro-forecast-list__forecast-timestamp">{{ formatUpatedTime(forecast.timestamp_published_local) }} &bull; {{ getForecasterInitals(forecast.forecaster_member_id) }}</p>
                    </template>
                  </ProForecastListItem>
                </ul>
              </div>           
            </div>
          </DataContainer>
        </template>
      </DataContainerSection>  

      <section>
        <ul class="jwx-block jwx-table-view" data-surface="1">
          <li class="jwx-table-view__row">
            <button
              v-if="!isExtended"
              type="button"
              @click="isExtended = true"
              class="jwx-table-view__action-row"
              data-type="button"
            >
              <p class="jwx-table-view__row-label">Pro Extended Forecasts</p>
              <div class="jwx-table-view__action-row-symbol">
                <Icon name="chevron-right" />
              </div>
            </button>
          </li>
        </ul>
      </section>
    </div>
  </div>
  <Suspense>
    <ModalSiteSettings :show="userStore.showSettingsModal" />
  </Suspense> 
  <ModalBrandPromo v-if="showPromoModal" />
</template>
